import { MapPin, Clock, Car, Accessibility, Building, TrainFront } from "lucide-react";
import { Reveal, SectionHead, GoldBtn, PageHero, AddToCalendar } from "@/components/Shared";
import EventMap from "@/components/EventMap";
import { EVENT } from "@/data/content";

const INFO = [
    { icon: TrainFront, title: "How to Reach", text: "MSIT & MSI are well connected by the Delhi Metro and major arterial roads of West Delhi. A detailed travel advisory with the nearest stations and gates will be announced closer to the festival." },
    { icon: Clock, title: "Event Hours", text: "The bazaar runs across three full days with evening experiences. Detailed gate hours — to be announced." },
    { icon: Car, title: "Parking", text: "On-campus and overflow parking will be organised for exhibitors and visitors. Parking plan — to be announced." },
    { icon: Accessibility, title: "Accessibility", text: "The festival is being designed with step-free access, accessible seating and assistance desks across zones." },
    { icon: Building, title: "Nearby Stay", text: "New Delhi offers stay options across every budget within easy reach of the venue. A curated stay guide — to be announced." },
    { icon: MapPin, title: "City Guide", text: "Make the most of the capital — a MahaBazaar city guide for food, culture and transit will be published before the festival." },
];

const VenuePlan = () => (
    <svg viewBox="0 0 800 420" className="w-full" fill="none" aria-label="Abstract venue plan of MSIT and MSI campus">
        <rect x="30" y="30" width="740" height="360" stroke="rgba(201,162,75,0.35)" strokeWidth="1.2" />
        <rect x="44" y="44" width="712" height="332" stroke="rgba(201,162,75,0.14)" strokeWidth="0.8" />
        <path d="M120 330 V180 Q120 110 190 110 Q260 110 260 180 V330" stroke="var(--gold)" strokeWidth="1.2" />
        <path d="M145 330 V190 Q145 135 190 135 Q235 135 235 190 V330" stroke="rgba(201,162,75,0.5)" strokeWidth="0.8" />
        <path d="M330 330 V150 Q330 80 400 80 Q470 80 470 150 V330" stroke="var(--gold)" strokeWidth="1.2" />
        <path d="M357 330 V160 Q357 106 400 106 Q443 106 443 160 V330" stroke="rgba(201,162,75,0.5)" strokeWidth="0.8" />
        <path d="M540 330 V180 Q540 110 610 110 Q680 110 680 180 V330" stroke="var(--gold)" strokeWidth="1.2" />
        <path d="M565 330 V190 Q565 135 610 135 Q655 135 655 190 V330" stroke="rgba(201,162,75,0.5)" strokeWidth="0.8" />
        <line x1="80" y1="355" x2="720" y2="355" stroke="var(--champagne)" strokeWidth="1" className="dash-flow" />
        {[150, 300, 470, 640].map((x) => (
            <circle key={x} cx={x} cy="355" r="3.5" fill="var(--gold)" className="pulse-soft" />
        ))}
        <text x="400" y="52" textAnchor="middle" fill="rgba(232,207,143,0.75)" fontSize="11" letterSpacing="6" style={{ fontFamily: "Jost, sans-serif" }}>
            MSIT &amp; MSI · NEW DELHI
        </text>
        <text x="400" y="395" textAnchor="middle" fill="rgba(168,158,140,0.7)" fontSize="9" letterSpacing="4" style={{ fontFamily: "Jost, sans-serif" }}>
            ABSTRACT VENUE PLAN · NOT TO SCALE
        </text>
    </svg>
);

export default function Venue() {
    return (
        <div data-testid="venue-page">
            <PageHero
                testid="venue-hero"
                eyebrow="The Venue"
                title={<>The City of Startups — <span className="gold-text">New Delhi</span></>}
                sub="MSIT & MSI transform into a futuristic innovation bazaar for three days — golden arches, glowing pavilions and a festival floor built for discovery."
            >
                <GoldBtn to="/register" testid="venue-register-btn">Register Now</GoldBtn>
                <AddToCalendar className="self-center" />
            </PageHero>

            <section className="py-24 md:py-32">
                <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
                    <Reveal>
                        <span className="eyebrow no-lines">Venue Overview</span>
                        <h2 className="font-display font-medium text-4xl sm:text-5xl leading-[1.06] mt-6">
                            {EVENT.venue.split(",")[0]}, <span className="gold-text">New Delhi</span>
                        </h2>
                        <p className="mt-6 text-muted-luxe text-base md:text-lg leading-relaxed">
                            Two institutional campuses become one grand festival ground — the Innovation Bazaar,
                            MahaTalks stage, PitchYatra arena, Investor Adda, Knowledge Labs and the evening
                            lawns, all within a single walkable venue.
                        </p>
                        <div className="mt-8 flex items-center gap-4 text-[0.62rem] tracking-[0.3em] uppercase text-[var(--champagne)]/85">
                            <span>{EVENT.dates}</span>
                            <span className="w-1.5 h-1.5 rotate-45 bg-[var(--gold)]" />
                            <span>{EVENT.venue}</span>
                        </div>
                    </Reveal>
                    <Reveal delay={150}>
                        <div className="card-luxe p-6 md:p-8"><VenuePlan /></div>
                    </Reveal>
                </div>
            </section>

            <section className="py-24 md:py-32 bg-[#1C060C]/60">
                <div className="container-x">
                    <SectionHead eyebrow="Plan Your Visit" title={<>Everything Before <span className="gold-text">You Arrive</span></>} />
                    <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {INFO.map((i, idx) => (
                            <Reveal key={i.title} delay={(idx % 3) * 80}>
                                <div className="card-luxe p-7 h-full" data-testid={`venue-info-${idx}`}>
                                    <div className="card-glow" />
                                    <i.icon size={20} strokeWidth={1.2} className="text-[var(--gold)]" />
                                    <h3 className="font-display text-xl mt-5 text-[var(--ivory)]">{i.title}</h3>
                                    <p className="mt-3 text-sm text-muted-luxe leading-relaxed">{i.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-24 md:py-32 scroll-mt-24" id="map">
                <div className="container-x">
                    <SectionHead eyebrow="Interactive Event Map" title={<>Navigate the <span className="gold-text">Bazaar</span></>}
                        sub="Zoom, pan, search and filter the zones — then get directions from the registration gates." />
                    <Reveal className="mt-14"><EventMap /></Reveal>
                </div>
            </section>
        </div>
    );
}
